import { useState, useRef, useEffect } from "react";
import { Box, Input, Button, Text, Spinner } from "@chakra-ui/react";
import styled from "styled-components";
import { useAdminAuth } from "../../contexts/AdminAuthContext";
import {
  streamChat,
  Message,
  getChatMessages,
  saveChatMessage,
} from "../../api/admin/adminChat";
import { Campaign } from "../../api/admin/adminCampaign";

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const MessageBubble = styled.div<{ $isUser: boolean }>`
  max-width: 80%;
  align-self: ${(props) => (props.$isUser ? "flex-end" : "flex-start")};
  background: ${(props) => (props.$isUser ? "#339CFF" : "#f1f5f9")};
  color: ${(props) => (props.$isUser ? "white" : "#1a202c")};
  padding: 0.75rem 1rem;
  border-radius: 12px;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const InputContainer = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: white;
  display: flex;
  gap: 0.75rem;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 1rem;
  color: #64748b;
  text-align: center;
  padding: 2rem;
`;

interface Props {
  selectedClientId: string;
  currentCampaign?: Campaign | null;
  onCampaignUpdate: (campaign: Campaign | null) => void;
  onAiSuggestions: (suggestions: any) => void;
  onUserInteraction: () => void;
}

export const ChatInterface = ({
  selectedClientId,
  currentCampaign,
  onCampaignUpdate,
  onAiSuggestions,
  onUserInteraction,
}: Props) => {
  const { adminToken } = useAdminAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const streamingMessageRef = useRef("");

  const parseAiSuggestions = (text: string) => {
    const suggestions: any = {};

    // Parse campaign name (from "Title:" or "Name:")
    const nameMatch = text.match(/(?:Title|Name):\s*(.+?)(?:\n|$)/i);
    if (nameMatch) suggestions.name = nameMatch[1].trim();

    // Parse goal amount
    const goalMatch = text.match(/Goal:\s*\$?([\d,]+)/i);
    if (goalMatch) suggestions.goalAmount = goalMatch[1].replace(/,/g, "");

    // Parse description (multiline support)
    const descMatch = text.match(
      /Description:\s*([\s\S]+?)(?:\n(?:Location|Tagline|Expires|Suggested)|\n\n|$)/i
    );
    if (descMatch)
      suggestions.description = descMatch[1].trim().replace(/\n/g, " ");

    // Parse tagline (shorter format)
    const taglineMatch = text.match(/Tagline:\s*(.+?)(?:\n|$)/i);
    if (taglineMatch) suggestions.tagline = taglineMatch[1].trim();

    // Parse expiration date (format: "Expires: 2025-10-12 17:00")
    const expiresMatch = text.match(
      /Expires:\s*(\d{4}-\d{2}-\d{2})\s+(\d{2}:\d{2})/i
    );
    if (expiresMatch) {
      suggestions.expires = `${expiresMatch[1]}T${expiresMatch[2]}`;
    }

    // Parse messages from numbered list with dates and quotes
    // Format: 1. [2024-10-11 10:00] "Message text"
    const messageMatches = text.match(
      /\d+\.\s*\[(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2})\]\s*[""](.{20,160})[""](?:\s*\n|$)/g
    );
    if (messageMatches) {
      suggestions.messages = messageMatches
        .map((m: string) => {
          const match = m.match(
            /\[(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2})\]\s*[""](.+?)[""]$/
          );
          if (match) {
            return {
              text: match[2].trim(),
              scheduledFor: match[1].replace(" ", "T"), // Convert to datetime-local format
            };
          }
          return null;
        })
        .filter(Boolean)
        .slice(0, 4);
    } else {
      // Fallback: try to parse without dates (old format)
      const fallbackMatches = text.match(
        /\d+\.\s*[""](.{20,160})[""](?:\s*\n|$)/g
      );
      if (fallbackMatches) {
        suggestions.messages = fallbackMatches
          .map((m: string) => ({
            text: m
              .replace(/^\d+\.\s*[""]/, "")
              .replace(/[""]$/, "")
              .trim(),
            scheduledFor: undefined,
          }))
          .slice(0, 4);
      }
    }

    return suggestions;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Reset chat when client changes
    setMessages([]);
    streamingMessageRef.current = "";
  }, [selectedClientId]);

  useEffect(() => {
    // Load chat history when campaign changes
    const loadChatHistory = async () => {
      if (!adminToken || !currentCampaign?.id) {
        setMessages([]);
        streamingMessageRef.current = ""; // Also clear streaming ref
        return;
      }

      try {
        const history = await getChatMessages(adminToken, currentCampaign.id);
        setMessages(history);
      } catch (error) {
        console.error("Failed to load chat history:", error);
      }
    };

    loadChatHistory();
  }, [currentCampaign?.id, adminToken]);

  const handleSend = async () => {
    if (!input.trim() || !adminToken || !selectedClientId) return;

    // Notify parent that user has interacted
    onUserInteraction();

    const userMessage: Message = { role: "user", content: input.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsStreaming(true);
    streamingMessageRef.current = "";

    // Save user message if there's a campaign
    if (currentCampaign?.id) {
      try {
        await saveChatMessage(
          adminToken,
          currentCampaign.id,
          "user",
          userMessage.content
        );
      } catch (error) {
        console.error("Failed to save user message:", error);
      }
    }

    // Add empty assistant message that we'll update
    setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

    await streamChat(
      adminToken,
      [...messages, userMessage],
      selectedClientId,
      currentCampaign,
      (text) => {
        streamingMessageRef.current += text;
        setMessages((prev) => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = {
            role: "assistant",
            content: streamingMessageRef.current,
          };
          return newMessages;
        });
      },
      async () => {
        setIsStreaming(false);
        const fullResponse = streamingMessageRef.current;

        // Save assistant message if there's a campaign
        if (currentCampaign?.id) {
          try {
            await saveChatMessage(
              adminToken,
              currentCampaign.id,
              "assistant",
              fullResponse
            );
          } catch (error) {
            console.error("Failed to save assistant message:", error);
          }
        }

        const suggestions = parseAiSuggestions(fullResponse);
        if (Object.keys(suggestions).length > 0) {
          onAiSuggestions(suggestions);
        }
        streamingMessageRef.current = "";
      },
      (error) => {
        console.error("Chat error:", error);
        setIsStreaming(false);
        setMessages((prev) => {
          const newMessages = [...prev];
          newMessages[newMessages.length - 1] = {
            role: "assistant",
            content: `Error: ${error}`,
          };
          return newMessages;
        });
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!selectedClientId) {
    return (
      <ChatContainer>
        <EmptyState>
          <Text fontSize="xl" fontWeight="600">
            Select a Client
          </Text>
          <Text>
            Choose a client from the dropdown above to start building a campaign
          </Text>
        </EmptyState>
      </ChatContainer>
    );
  }

  return (
    <ChatContainer>
      <MessagesContainer>
        {messages.length === 0 && (
          <EmptyState>
            {currentCampaign ? (
              <>
                <Text fontSize="lg" fontWeight="600">
                  Editing: {currentCampaign.name} ✏️
                </Text>
                <Text>
                  I'm here to help you with this campaign! I can:
                  <br />• Create strategic text messages based on your campaign
                  details
                  <br />• Suggest improvements to messaging or timing
                  <br />• Help refine your campaign strategy
                  <br />
                  <br />
                  What would you like help with?
                </Text>
              </>
            ) : (
              <>
                <Text fontSize="lg" fontWeight="600">
                  Let's build a campaign! 🚀
                </Text>
                <Text>
                  Tell me about the event or campaign you want to create.
                  Include details like:
                  <br />• Event name and purpose
                  <br />• Location and date
                  <br />• Fundraising goals
                </Text>
                <Box
                  mt={4}
                  p={3}
                  bg="#f1f5f9"
                  borderRadius="8px"
                  fontSize="sm"
                  borderLeft="3px solid #339CFF"
                >
                  <Text fontWeight="600" mb={2} color="#64748b">
                    Example:
                  </Text>
                  <Text color="#475569" fontStyle="italic">
                    "We're hosting our Annual Gala Fundraiser on December 15th
                    at the Grand Ballroom. The event celebrates our community's
                    achievements and raises funds for local youth programs. Our
                    goal is to raise $50,000 to support after-school activities
                    for underserved children."
                  </Text>
                </Box>
              </>
            )}
          </EmptyState>
        )}
        {messages.map((msg, idx) => (
          <MessageBubble key={idx} $isUser={msg.role === "user"}>
            {msg.content}
          </MessageBubble>
        ))}
        {isStreaming && messages[messages.length - 1]?.content === "" && (
          <Box alignSelf="flex-start">
            <Spinner size="sm" color="#339CFF" />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </MessagesContainer>
      <InputContainer>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Describe your campaign..."
          disabled={isStreaming}
          size="md"
        />
        <Button
          onClick={handleSend}
          isLoading={isStreaming}
          colorScheme="blue"
          size="md"
          minWidth="80px"
        >
          Send
        </Button>
      </InputContainer>
    </ChatContainer>
  );
};
